import React, { useCallback } from 'react';

import { useDropzone } from 'react-dropzone';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { Box, Card, CardActionArea, CardContent, CircularProgress, Stack, Typography } from '@mui/material';

import { ALLOWED_IMAGE_MIMETYPES } from '~/services/ImageService';

interface Props {
  disabled?: boolean;
  helperText?: string;
  isBusy?: boolean;
  label?: string;
  onSelectFiles?: (files: Array<File>) => Promise<void> | void;
}

export default function ImageUploadTile({
  disabled = false,
  helperText,
  isBusy = false,
  label = 'Upload images',
  onSelectFiles,
}: Props) {
  const handleDrop = useCallback(
    async (acceptedFiles: Array<File>) => {
      if (!acceptedFiles.length || disabled || !onSelectFiles) {
        return;
      }

      await onSelectFiles(acceptedFiles);
    },
    [disabled, onSelectFiles],
  );

  const accept = ALLOWED_IMAGE_MIMETYPES;

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept,
    disabled: disabled || isBusy || !onSelectFiles,
    multiple: true,
    onDrop: handleDrop,
  });

  return (
    <Card
      variant="outlined"
      sx={{
        minHeight: 470,
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: isDragActive ? 'primary.main' : 'divider',
        bgcolor: isDragActive ? 'action.hover' : 'background.paper',
      }}
    >
      <CardActionArea
        {...getRootProps()}
        sx={{
          height: '100%',
        }}
      >
        <input {...getInputProps()} />
        <CardContent
          sx={{
            height: '100%',
          }}
        >
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: '100%', textAlign: 'center' }}>
            {isBusy ? (
              <CircularProgress size={32} />
            ) : (
              <AddPhotoAlternateOutlinedIcon fontSize="large" color="action" />
            )}
            <Box>
              <Typography variant="h6">{isBusy ? 'Preparing...' : label}</Typography>
              <Typography color="text.secondary" variant="body2">
                Drop images here or click to browse
              </Typography>
              {helperText ? (
                <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
                  {helperText}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
